import React, { useState, useEffect } from 'react'
import IconPicker from './IconPicker'
import DynamicIcon from '../DynamicIcon'

const ProductForm = ({ product, onSubmit, onCancel, loading }) => {
  const [form, setForm] = useState({
    name: '',
    description: '',
    category: 'telegram',
    icon: 'Package',
    image_url: '',
    g2bulk_catalog_id: '',
    g2bulk_endpoint: '/v1/orders',
    g2bulk_validation_endpoint: '',
    denomination: '',
    denomination_value: '',
    requires_user_input: true,
    user_input_label: 'Username',
    user_input_placeholder: '',
    user_input_validation_regex: '',
    captcha_frequency: 30,
    is_active: true,
    sort_order: 0,
  })

  useEffect(() => {
    if (product) {
      setForm({
        name: product.name || '',
        description: product.description || '',
        category: product.category || 'telegram',
        icon: product.icon || 'Package',
        image_url: product.image_url || '',
        g2bulk_catalog_id: product.g2bulk_catalog_id || '',
        g2bulk_endpoint: product.g2bulk_endpoint || '/v1/orders',
        g2bulk_validation_endpoint: product.g2bulk_validation_endpoint || '',
        denomination: product.denomination || '',
        denomination_value: product.denomination_value || '',
        requires_user_input: product.requires_user_input ?? true,
        user_input_label: product.user_input_label || 'Username',
        user_input_placeholder: product.user_input_placeholder || '',
        user_input_validation_regex: product.user_input_validation_regex || '',
        captcha_frequency: product.captcha_frequency ?? 30,
        is_active: product.is_active ?? true,
        sort_order: product.sort_order || 0,
      })
    }
  }, [product])

  const handleChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }))
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    onSubmit({
      ...form,
      denomination_value: form.denomination_value ? parseFloat(form.denomination_value) : 0,
      captcha_frequency: parseInt(form.captcha_frequency) || 30,
      sort_order: parseInt(form.sort_order) || 0,
    })
  }

  const categories = [
    { value: 'telegram', label: 'Telegram' },
    { value: 'pubgm', label: 'PUBG Mobile' },
    { value: 'gaming', label: 'Gaming' },
    { value: 'giftcard', label: 'Gift Cards' },
    { value: 'other', label: 'Other' },
  ]

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Basic Info */}
      <div>
        <h3 className="text-sm font-semibold text-text-secondary uppercase tracking-wider mb-3">
          Basic Info
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2">
            <label className="label mb-1 block">Product Name *</label>
            <input
              type="text"
              value={form.name}
              onChange={(e) => handleChange('name', e.target.value)}
              placeholder="Telegram Premium 3 Months"
              className="input"
              required
            />
          </div>

          <div>
            <label className="label mb-1 block">Category</label>
            <select
              value={form.category}
              onChange={(e) => handleChange('category', e.target.value)}
              className="input"
            >
              {categories.map(c => (
                <option key={c.value} value={c.value}>{c.label}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="label mb-1 block">Icon</label>
            <IconPicker
              value={form.icon}
              onChange={(iconName) => handleChange('icon', iconName)}
            />
          </div>

          <div className="md:col-span-2">
            <label className="label mb-1 block">Description</label>
            <textarea
              value={form.description}
              onChange={(e) => handleChange('description', e.target.value)}
              placeholder="Short description shown to users"
              className="input h-20 resize-none"
              rows={3}
            />
          </div>

          <div>
            <label className="label mb-1 block">Denomination *</label>
            <input
              type="text"
              value={form.denomination}
              onChange={(e) => handleChange('denomination', e.target.value)}
              placeholder="3 Months"
              className="input"
              required
            />
          </div>

          <div>
            <label className="label mb-1 block">Denomination Value</label>
            <input
              type="number"
              step="0.01"
              value={form.denomination_value}
              onChange={(e) => handleChange('denomination_value', e.target.value)}
              placeholder="0.00"
              className="input"
            />
          </div>

          <div>
            <label className="label mb-1 block">Image URL</label>
            <input
              type="url"
              value={form.image_url}
              onChange={(e) => handleChange('image_url', e.target.value)}
              placeholder="https://..."
              className="input"
            />
          </div>

          <div>
            <label className="label mb-1 block">Sort Order</label>
            <input
              type="number"
              value={form.sort_order}
              onChange={(e) => handleChange('sort_order', e.target.value)}
              className="input"
            />
          </div>
        </div>
      </div>

      {/* G2Bulk Integration */}
      <div>
        <h3 className="text-sm font-semibold text-text-secondary uppercase tracking-wider mb-3">
          G2Bulk Integration
        </h3>
        <div className="grid grid-cols-1 gap-4">
          <div>
            <label className="label mb-1 block">G2Bulk Catalog ID *</label>
            <input
              type="text"
              value={form.g2bulk_catalog_id}
              onChange={(e) => handleChange('g2bulk_catalog_id', e.target.value)}
              placeholder="catalog-id"
              className="input"
              required
            />
          </div>

          <div>
            <label className="label mb-1 block">Order Endpoint</label>
            <input
              type="text"
              value={form.g2bulk_endpoint}
              onChange={(e) => handleChange('g2bulk_endpoint', e.target.value)}
              placeholder="/v1/orders"
              className="input"
            />
          </div>

          <div>
            <label className="label mb-1 block">Validation Endpoint</label>
            <input
              type="text"
              value={form.g2bulk_validation_endpoint}
              onChange={(e) => handleChange('g2bulk_validation_endpoint', e.target.value)}
              placeholder="/v1/validate/telegram"
              className="input"
            />
          </div>
        </div>
      </div>

      {/* User Input Config */}
      <div>
        <h3 className="text-sm font-semibold text-text-secondary uppercase tracking-wider mb-3">
          User Input
        </h3>
        <div className="space-y-4">
          <label className="flex items-center space-x-3 cursor-pointer">
            <input
              type="checkbox"
              checked={form.requires_user_input}
              onChange={(e) => handleChange('requires_user_input', e.target.checked)}
              className="w-4 h-4 rounded border-border bg-background text-accent focus:ring-accent"
            />
            <span className="text-text-primary text-sm">Requires user input (username/player ID)</span>
          </label>

          {form.requires_user_input && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pl-7">
              <div>
                <label className="label mb-1 block">Input Label</label>
                <input
                  type="text"
                  value={form.user_input_label}
                  onChange={(e) => handleChange('user_input_label', e.target.value)}
                  placeholder="Telegram Username"
                  className="input"
                />
              </div>

              <div>
                <label className="label mb-1 block">Placeholder</label>
                <input
                  type="text"
                  value={form.user_input_placeholder}
                  onChange={(e) => handleChange('user_input_placeholder', e.target.value)}
                  placeholder="@username"
                  className="input"
                />
              </div>

              <div className="md:col-span-2">
                <label className="label mb-1 block">Validation Regex</label>
                <input
                  type="text"
                  value={form.user_input_validation_regex}
                  onChange={(e) => handleChange('user_input_validation_regex', e.target.value)}
                  placeholder="^@[a-zA-Z0-9_]{5,32}$"
                  className="input font-mono text-xs"
                />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Security */}
      <div>
        <h3 className="text-sm font-semibold text-text-secondary uppercase tracking-wider mb-3">
          Security
        </h3>
        <div className="space-y-3">
          <div>
            <label className="label mb-1 block">
              Captcha Frequency (every N redemptions, 0 = always)
            </label>
            <input
              type="range"
              min="0"
              max="100"
              value={form.captcha_frequency}
              onChange={(e) => handleChange('captcha_frequency', e.target.value)}
              className="w-full"
            />
            <div className="text-text-muted text-xs mt-1">
              Current: {form.captcha_frequency === 0 ? 'Always' : `Every ${form.captcha_frequency} redemptions`}
            </div>
          </div>

          <label className="flex items-center space-x-3 cursor-pointer">
            <input
              type="checkbox"
              checked={form.is_active}
              onChange={(e) => handleChange('is_active', e.target.checked)}
              className="w-4 h-4 rounded border-border bg-background text-accent focus:ring-accent"
            />
            <span className="text-text-primary text-sm">Active (visible to public)</span>
          </label>
        </div>
      </div>

      {/* Actions */}
      <div className="flex items-center space-x-3 pt-4 border-t border-border">
        <button type="submit" disabled={loading} className="btn btn-primary">
          {loading ? (
            <span className="flex items-center space-x-2">
              <span className="spinner w-4 h-4"></span>
              <span>Saving...</span>
            </span>
          ) : product ? 'Update Product' : 'Create Product'}
        </button>
        <button type="button" onClick={onCancel} className="btn btn-secondary">Cancel</button>
      </div>
    </form>
  )
}

export default ProductForm